import React, { memo, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { addDays, subMonths, isAfter } from 'date-fns';
import { IconButton } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import {
  getOptions,
  HighestEducation,
  windstormMitigationDiscountStates,
  stormShuttersStates,
  hideHighestEducationStates,
  windstormMitigationDiscountYearBuilt,
  stormShuttersInAdvancedWindstormSectionStates,
  fortifiedHomeStates,
  lookupsJson,
  policyType
} from '@ourbranch/lookups';
import { enhancedRoofWindstormStates } from '@ourbranch/roof-helpers';
import config from 'aws-exports';

import { useRouteHelper } from 'core/hooks/use-route-helper';
import { useToast } from 'core/components/toast';
import { tooltipHoverTexts } from 'core/helpers/constants';
import { useStore } from 'core/store/store.mobx';
import { DwellingAddress } from 'common/components/dwelling-address';
import { useCurrentState } from 'common/hooks/useCurrentState';
import useOptions from 'core/hooks/use-options';
import CollapsibleCard from 'core/components/collapsible-card';
import { FieldWithTooltip } from 'core/components/field-with-tooltip';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import Section from 'core/components/section';
import FormList from 'core/components/form-list';
import withDatePicker from 'core/components/with-date-picker';
import { NotificationCard } from 'core/components/notification-card';
import { Tooltip } from 'core/components/tooltip';
import useSession from 'core/hooks/use-session';
import NotificationIconGreen from 'core/assets/svg/ic-info-dark.svg';
import InfoBlackIcon from 'core/assets/svg/ic-info-black.svg';
import ResetIcon from 'core/assets/svg/ic-reset.svg';
import PencilIcon from 'core/assets/svg/pencil-orange.svg';
import LockedIcon from 'core/assets/svg/ic-locked.svg';
import QuestionIcon from 'core/assets/svg/info-small.svg';
import { generateZillowLink } from 'core/helpers/property-view-links';
import { currencyFormatter } from 'core/helpers/formatters';
import { haveHomeReportsRun } from 'core/helpers/mvr-helpers';
import { setPrimaryMortgageValues } from 'offer/helpers/mortgage-helpers';
import { RCEModal } from './rce-modal';
import AdvancedWindstormDetails from './advanced-windstorm-details/advanced-windstorm-details';
import { ProtectionClassOverride } from 'common/components/protection-class-override';
import { StormShutters } from './storm-shutters';
import { DataSourceMismatchNotification } from './data-source-mismatch-notification';
import { AddMortgage, MortgageItem } from '../../mortgage-details';
import externalLinkIcon from 'core/assets/svg/external-link.svg';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';
import { brandConstants } from 'brand-config';

import useStyles from './detail.styles';

const id = 'home';
const ROOF_SHAPE_GUIDE_ID = 'e6d2dadb-66c8-46d1-9aee-b51df3ce2e69';

const Detail = memo(function Detail({ disabled, fromPolicy }) {
  const { offer: store } = useStore();
  const classes = useStyles();
  const { canEditUneditablePropertyDetailsFields, isAgency, isInternalAgent } = useSession();
  const isAgentSold = store.offer?.quote?.isAgentSold;
  const { values, setFieldValue, setFieldTouched, initialValues } = useFormikContext();
  const { home } = values;
  const state = useCurrentState(values, { policyType: policyType.Home });
  // Brand-aware so CFM/MO gets its extra Exterior Wall Finish options (Stone Veneer Natural, Log
  // Siding); every other brand falls back to the base list. #34910
  const exteriorWallTypeOptions = useOptions('exteriorWallType', state);
  const showStormShutters = stormShuttersStates.includes(state);
  const showEnhancedRoofWindstorm = enhancedRoofWindstormStates[state];
  const showWindstormMitigationLevel =
    windstormMitigationDiscountStates[state] &&
    (!home.yearBuilt || home.yearBuilt < windstormMitigationDiscountYearBuilt);
  const showAdvancedWindstormDetails = showEnhancedRoofWindstorm || showWindstormMitigationLevel || showStormShutters;
  const showStormShuttersInAdvancedSection =
    stormShuttersStates.includes(state) && stormShuttersInAdvancedWindstormSectionStates.includes(state);
  const showStormShuttersInRoofSection = stormShuttersStates.includes(state) && !showStormShuttersInAdvancedSection;
  const mortgageDetails = home.mortgageDetails;
  const replacementCostTotal = home.replacementCostEstimate?.total;
  const estimatedCost =
    replacementCostTotal != null ? currencyFormatter(replacementCostTotal, { removeDecimal: true }) : null;
  const toast = useToast();
  const { inOfferEditRoute, inPolicyRoute } = useRouteHelper();

  const sixMonthsAgo = subMonths(new Date(), 6);
  const homePurchasedWithinSixMonths = isAfter(new Date(values.home.purchaseDate), sixMonthsAgo);

  const location = home.homeLocation;
  const address = `${location.address} ${location.address2 ?? ''} ${location.city} ${location.state} ${location.zip}`;

  useEffect(() => {
    if (mortgageDetails.length > 0) {
      if (!mortgageDetails.find((item) => item.primary)) {
        const mortgage = mortgageDetails[0];
        setFieldValue(`${id}.mortgageDetails.0`, { ...mortgage, primary: true });
        setPrimaryMortgageValues(setFieldValue, mortgage, values.primaryMortgageDetail);
      }
    }
  }, [mortgageDetails, setFieldValue]);

  const toastRCEWarning = useCallback(() => {
    if (
      home.replacementCostEstimate?.total != null &&
      home.replacementCostEstimate.total !== values.homeCoverage.coverageA
    ) {
      toast.notify({
        type: 'success',
        message:
          'Changing this item may affect the dwelling limit. After updating the offer, review the dwelling limit to ensure it is correct.'
      });
    }
  }, [toast, values.homeCoverage.coverageA, home.replacementCostEstimate?.total]);

  //Roof year notification only shows if the offer roofYear has been updated after pulling Cape data and is newer than the year provided by Cape.

  const { offer } = store;
  const { roofReplacementYearMax } = offer?.quote?.home || {};
  const skippedDataPulls = offer?.skippedDataPulls;
  const isCapePulled = !skippedDataPulls?.includes('cape-analytics');
  const showChangedRoofYearNotification =
    inOfferEditRoute &&
    isCapePulled &&
    roofReplacementYearMax !== null &&
    home.roofYear !== roofReplacementYearMax && // home !=== offer.quote.home when updating offer, clears roofReplacementYearMax on update if use home.roofReplacementYearMax
    home.roofYear > roofReplacementYearMax;

  const roofYearNotificationText = !isAgentSold
    ? {
        header: 'Verify roof year before binding',
        body: `Please ensure the information entered is correct by verifying proof prior to bind. 
    Acceptable proof includes a paid invoice or signed contract with the completion date. 
    If the roof was replaced before ownership, roof permits or seller’s disclosure are also accepted. 
    If proof is unavailable, reset the roof year to on or before ${roofReplacementYearMax}. `
      }
    : {
        header: 'Due to the roof year entered, we will require proof post-purchase',
        body: `The roof year entered is newer than what was pulled from ${brandConstants.SHORT_NAME}’s satellite imagery data source (${roofReplacementYearMax}) and will require acceptable proof post-purchase.`
      };

  const [rceModalOpen, setRceModalOpen] = useState(false);
  const rc1 = offer?.quote?.overrides?.rc1;
  const ourReplacementCost = rc1?.calculated?.rce;
  const agentReplacementCost = values.replacementCostSelection?.agentReplacementCost ?? rc1?.input?.coverageA;
  const useCalculatedReplacementCost = values.replacementCostSelection?.useCalculated ?? Boolean(rc1?.useCalculatedRce);
  const showReplacementCostControl = !fromPolicy && !disabled;
  const canEditReplacementCost =
    showReplacementCostControl &&
    Boolean(agentReplacementCost && ourReplacementCost) &&
    Boolean(skippedDataPulls?.includes('remove-unverified-rc1-values'));
  const usingAgentReplacementCost = canEditReplacementCost && !useCalculatedReplacementCost;
  const visibleValuationId = isInternalAgent ? home.replacementCostEstimate?.valuationId : null;
  let replacementCostTooltipText;
  if (!canEditReplacementCost) {
    replacementCostTooltipText = tooltipHoverTexts.rceText(visibleValuationId);
  } else if (usingAgentReplacementCost) {
    replacementCostTooltipText = tooltipHoverTexts.rceAgentValueText;
  } else {
    replacementCostTooltipText = tooltipHoverTexts.rceCalculatedValueText(visibleValuationId);
  }

  const handleReplacementCostChange = useCallback(
    ({ useCalculated, agentReplacementCost: nextAgentReplacementCost }) => {
      const nextTotal = useCalculated ? ourReplacementCost : nextAgentReplacementCost;

      setFieldValue('replacementCostSelection', { useCalculated, agentReplacementCost: nextAgentReplacementCost });
      setFieldValue(`${id}.replacementCostEstimate.total`, nextTotal);
    },
    [setFieldValue, ourReplacementCost]
  );

  if (!home) {
    return null;
  }
  return (
    <Section
      // Scroll anchor for the validation-errors modal "Home details" go-to (see
      // validation-errors-modal.constants.js SECTION_NAV_TARGETS). `id` is 'home'.
      id={id}
      title="Home details"
      type="SubSection"
      right={
        <Grid container className={classes.links}>
          <Grid className={classes.linkAndIcon}>
            <a
              rel="noreferrer noopener"
              target="_blank"
              href={`https://maps.googleapis.com/maps/api/streetview?size=1200x800&location=${home.latitude},${home.longitude}&fov=80&key=${config.googleGeocoderKey}`}
              className={classes.streetMapLink}
            >
              Street View
              <img src={externalLinkIcon} alt="link to google maps" />
            </a>
          </Grid>
          <Grid className={classes.linkAndIcon}>
            <a
              rel="noreferrer noopener"
              target="_blank"
              href={`https://earth.google.com/web/search/=${address}`}
              className={classes.streetMapLink}
            >
              Google Earth
              <img src={externalLinkIcon} alt="link to google earth" />
            </a>
          </Grid>
          <Grid className={classes.linkAndIcon}>
            <a
              rel="noreferrer noopener"
              target="_blank"
              href={generateZillowLink(location)}
              className={classes.streetMapLink}
            >
              Zillow
              <img src={externalLinkIcon} alt="link to zillow" />
            </a>
          </Grid>
          {home.capeConditions?.roofAgeImage && (
            <Grid className={classes.linkAndIcon}>
              <a
                rel="noreferrer noopener"
                target="_blank"
                href={home.capeConditions.roofAgeImage}
                className={classes.streetMapLink}
              >
                Roof Age
                <img src={externalLinkIcon} alt="link to roof age" />
              </a>
            </Grid>
          )}
          {home.capeConditions?.roofConditionImage && (
            <Grid className={classes.linkAndIcon}>
              <a
                rel="noreferrer noopener"
                target="_blank"
                href={home.capeConditions.roofConditionImage}
                className={classes.streetMapLink}
              >
                Roof Condition
                <img src={externalLinkIcon} alt="link to roof condition" />
              </a>
            </Grid>
          )}
        </Grid>
      }
    >
      <CollapsibleCard
        initialOpen
        content={
          <DwellingAddress
            dwelling="Home"
            fieldName="home.homeLocation"
            location={location}
            includeApt={home.homeLocation.address2?.length > 0}
            disabled={disabled}
          />
        }
      >
        <Grid container justifyContent="space-around" className={classes.containerDark}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12}>
              <Label type="formSubTitleLight">Residents</Label>
            </Grid>
            <FormField
              name={`${id}.ownershipStatus`}
              type="select"
              label="Ownership"
              mode="light"
              options={getOptions('ownershipStatus')}
              xs={6}
            />

            <FormField
              name={`${id}.numOccupants`}
              type="numeric"
              label="Residents"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={6}
            />
            {!hideHighestEducationStates.includes(state) && (
              <FormField
                name="highestEducation"
                type="value"
                label="Highest Education of App/Co-App"
                value={HighestEducation[values.highestEducation] || 'N/A'}
                xs={6}
              />
            )}
            <FormField
              name="oldestResident"
              type="value"
              label="Age of Oldest App/Co-App"
              value={values.oldestResident || 'N/A'}
              xs={hideHighestEducationStates.includes(state) ? 12 : 6}
            />

            <Grid item xs={12}>
              <Label type="formSubTitleLight">Purchase Information</Label>
            </Grid>
            <FormField
              name={`${id}.purchaseDate`}
              type="date"
              label="House Purchased"
              mode="light"
              xs={6}
              disableFuture={false}
              maxDate={addDays(new Date(), 60)}
              defaultToEmpty
            />
            {homePurchasedWithinSixMonths && !['MI', 'MD', 'PA'].includes(state) && (
              <Grid item xs={6}>
                <FieldWithTooltip
                  name={`${id}.purchaseAmount`}
                  type="numeric"
                  label="Purchase Price"
                  mode="light"
                  xs={6}
                  tooltip={tooltipHoverTexts.purchaseAmount}
                  fast={false}
                  format={{ thousandSeparator: true, prefix: '$' }}
                />
              </Grid>
            )}
            {(!homePurchasedWithinSixMonths || ['MI', 'MD', 'PA'].includes(state)) && (
              <Grid item xs={6}>
                <FieldWithTooltip
                  name={`${id}.purchaseAmount`}
                  type="value"
                  label="Purchase Price"
                  mode="light"
                  xs={6}
                  tooltip={tooltipHoverTexts.purchaseAmount}
                  value={'N/A'}
                  labelNextToTooltip={!home.purchaseAmount}
                  fast={false}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Label type="formSubTitleLight">Property</Label>
            </Grid>
            {canEditUneditablePropertyDetailsFields || inOfferEditRoute ? (
              <>
                <FormField
                  name={`${id}.yearBuilt`}
                  type="numeric"
                  label="Year built in"
                  mode="light"
                  format={{
                    allowNegative: false
                  }}
                  xs={4}
                  onChange={(val) => {
                    if (inOfferEditRoute && val !== initialValues.home.yearBuilt) {
                      toastRCEWarning();
                    }
                  }}
                />
              </>
            ) : (
              <>
                <Grid item xs={4}>
                  <FieldWithTooltip
                    name={`${id}.yearBuilt`}
                    label="Year built in"
                    mode="light"
                    lockedValue={{
                      text: inOfferEditRoute
                        ? tooltipHoverTexts.uneditableFieldsTooltipText
                        : tooltipHoverTexts.uneditableFieldsPolicy
                    }}
                    type="value"
                  />
                </Grid>
              </>
            )}

            {(canEditUneditablePropertyDetailsFields || inOfferEditRoute) && (
              <FormField
                name={`${id}.sqFt`}
                type="numeric"
                label="Above-Ground SqFt"
                mode="light"
                format={{
                  allowNegative: false
                }}
                xs={4}
                onChange={(val) => {
                  if (inOfferEditRoute && val !== initialValues.home.sqFt) {
                    toastRCEWarning();
                  }
                }}
              />
            )}

            {!canEditUneditablePropertyDetailsFields && inPolicyRoute && (
              <Grid item xs={4}>
                <FieldWithTooltip
                  name={`${id}.sqFt`}
                  type="value"
                  label="Above-Ground SqFt"
                  mode="light"
                  lockedValue={{
                    text: tooltipHoverTexts.uneditableFieldsPolicy
                  }}
                />
              </Grid>
            )}

            <FormField
              name={`${id}.basementSqFt`}
              type="numeric"
              label="Basement SqFt"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.basementSqFt) {
                  toastRCEWarning();
                }
              }}
            />
            <DataSourceMismatchNotification id={id} name="yearBuilt" />
            <DataSourceMismatchNotification id={id} name="sqFt" />
            <FormField
              name={`${id}.typeOfHome`}
              type="select"
              label="Home Type"
              mode="light"
              options={getOptions('typeOfHome')}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.typeOfHome) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.numFamiliesInDwelling`}
              type="numeric"
              label="Families in Dwelling"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numFamiliesInDwelling) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.homeQuality`}
              type="select"
              label="Home Quality"
              mode="light"
              options={getOptions('homeQuality')}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.homeQuality) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.numStories`}
              type="numeric"
              label="Number of Stories"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={3}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numStories) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.numHalfBathrooms`}
              type="numeric"
              label="Number of Half Bathrooms"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={3}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numHalfBathrooms) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.numFullBathrooms`}
              type="numeric"
              label="Number of Full Bathrooms"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={3}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numFullBathrooms) {
                  toastRCEWarning();
                }
              }}
            />
            {canEditUneditablePropertyDetailsFields || inOfferEditRoute ? (
              <FormField
                name={`${id}.constructionType`}
                type="select"
                label="Construction Type"
                mode="light"
                options={getOptions('constructionType')}
                xs={3}
                onChange={(val) => {
                  if (inOfferEditRoute && val !== initialValues.home.constructionType) {
                    toastRCEWarning();
                  }
                }}
              />
            ) : (
              <Grid item xs={3}>
                <FieldWithTooltip
                  name={`${id}.constructionType`}
                  label="Construction Type"
                  mode="light"
                  lockedValue={{
                    text: inOfferEditRoute
                      ? tooltipHoverTexts.uneditableFieldsTooltipText
                      : tooltipHoverTexts.uneditableFieldsPolicy
                  }}
                  type="value"
                  formatter={(value) => lookupsJson.constructionType.find(({ id }) => id === value).value}
                  onChange={(val) => {
                    if (inOfferEditRoute && val !== initialValues.home.constructionType) {
                      toastRCEWarning();
                    }
                  }}
                />
              </Grid>
            )}
            <DataSourceMismatchNotification id={id} name="constructionType" />
            <FormField
              name={`${id}.numFireplaces`}
              type="numeric"
              label="Number of Fireplaces"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numFireplaces) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.numWoodBurningStoves`}
              type="numeric"
              label="Number of Wood Stoves"
              mode="light"
              format={{
                allowNegative: false
              }}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.numWoodBurningStoves) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.basementType`}
              type="select"
              label="Basement/Foundation Type"
              mode="light"
              options={getOptions('basementType')}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.basementType) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.exteriorWallType`}
              type="select"
              label="Exterior Wall finish"
              mode="light"
              options={exteriorWallTypeOptions}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.exteriorWallType) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.garageCarCapacity`}
              type="numeric"
              label="Garage's car space"
              mode="light"
              format={{
                allowNegative: false
              }}
              disabled={home.garageType === 'X'}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.garageCarCapacity) {
                  toastRCEWarning();
                }
              }}
            />
            <FormField
              name={`${id}.garageType`}
              type="select"
              label="Garage Type"
              mode="light"
              options={getOptions('garageType')}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.garageType) {
                  toastRCEWarning();
                }
              }}
            />
            {isAgency && fromPolicy ? (
              <Grid item xs={3}>
                <FieldWithTooltip
                  name={`${id}.roofYear`}
                  type="value"
                  label="Roof Year"
                  mode="light"
                  tooltip={tooltipHoverTexts.roofYearDisabled}
                  labelNextToTooltip
                />
              </Grid>
            ) : (
              <Grid item xs={4}>
                <FieldWithTooltip
                  name={`${id}.roofYear`}
                  type="numeric"
                  label="Roof Year"
                  mode="light"
                  tooltip={tooltipHoverTexts.roofYear}
                  format={{
                    allowNegative: false
                  }}
                  // using touched to know if we are focused on this field in the other-coverages for coverageRSExtended
                  onFocus={() => setFieldTouched(`${id}.roofYear`, true, true)}
                  onBlur={() => setFieldTouched(`${id}.roofYear`, false, true)}
                  onChange={(val) => {
                    if (inOfferEditRoute && val !== initialValues.home.roofYear) {
                      toastRCEWarning();
                    }
                  }}
                />
              </Grid>
            )}

            <FormField
              name={`${id}.roofType`}
              type="select"
              label="Roof Type"
              mode="light"
              options={getOptions('roofType', state)}
              xs={4}
              onChange={(val) => {
                if (inOfferEditRoute && val !== initialValues.home.roofType) {
                  toastRCEWarning();
                }
              }}
            />
            {canEditUneditablePropertyDetailsFields || inOfferEditRoute ? (
              <Grid item xs={4}>
                <FieldWithTooltip
                  name={`${id}.roofShape`}
                  type="select"
                  label="Roof Shape"
                  mode="light"
                  options={getOptions('roofShape')}
                  onChange={(val) => {
                    if (inOfferEditRoute && val !== initialValues.home.roofShape) {
                      toastRCEWarning();
                    }
                  }}
                  tooltip={
                    inOfferEditRoute && {
                      onClick: () => {
                        window.Appcues?.show(ROOF_SHAPE_GUIDE_ID);
                      },
                      label: 'View Guide',
                      onHoverText: 'Click to view guide',
                      clickIcon: arrowIcon
                    }
                  }
                />
              </Grid>
            ) : (
              <Grid item xs={3}>
                <FieldWithTooltip
                  name={`${id}.roofShape`}
                  label="Roof Shape"
                  mode="light"
                  options={getOptions('roofShape')}
                  lockedValue={{
                    text: inOfferEditRoute
                      ? tooltipHoverTexts.uneditableFieldsTooltipText
                      : tooltipHoverTexts.uneditableFieldsPolicy
                  }}
                  type="value"
                  formatter={(value) => lookupsJson.roofShape.find(({ id }) => id === value).value}
                  tooltip={
                    inOfferEditRoute && {
                      onClick: () => {
                        window.Appcues?.show(ROOF_SHAPE_GUIDE_ID);
                      },
                      label: 'View Guide',
                      onHoverText: 'Click to view guide',
                      clickIcon: arrowIcon
                    }
                  }
                />
              </Grid>
            )}

            <DataSourceMismatchNotification id={id} name="roofShape" />

            {!!showChangedRoofYearNotification && (
              <NotificationCard
                type="light"
                className={classes.cardStyling}
                iconByHeader
                icon={NotificationIconGreen}
                iconSize="icon16Bold"
                header={roofYearNotificationText.header}
              >
                <Grid container className={classes.notificationText}>
                  {roofYearNotificationText.body}
                  <Grid container className={classes.buttonContainer}>
                    {isAgentSold && (
                      <IconButton
                        color="secondary"
                        variant="text"
                        className={classes.button}
                        onClick={() => {
                          window?.Appcues?.show('4496af53-16c3-4c10-9027-2f585bb65e6a');
                        }}
                      >
                        <img src={arrowIcon} className={classes.iconStyle} alt="Learn More" />
                        <div className={classes.button}>Learn More</div>
                      </IconButton>
                    )}

                    <IconButton
                      color={'secondary'}
                      variant={'text'}
                      className={classes.button}
                      onClick={() => {
                        setFieldValue(`${id}.roofYear`, home.roofReplacementYearMax);
                        setFieldTouched(`${id}.roofYear`, true);
                      }}
                    >
                      <img src={ResetIcon} className={classes.iconStyle} alt="Reset roof year" />
                      Reset roof year
                    </IconButton>
                  </Grid>
                </Grid>
              </NotificationCard>
            )}

            {fortifiedHomeStates.includes(state) && (
              <Grid item xs={12}>
                <Grid item xs={6}>
                  <FieldWithTooltip
                    name={`${id}.fortifiedHome`}
                    id={`${id}.fortifiedHome`}
                    type="select"
                    label="Fortified Home Discount"
                    options={lookupsJson.fortifiedHome}
                    mode="light"
                    tooltip={tooltipHoverTexts.fortifiedHome(inOfferEditRoute, inPolicyRoute)}
                  />
                </Grid>
              </Grid>
            )}
            {showStormShuttersInRoofSection && <StormShutters />}
            {showStormShuttersInRoofSection && showAdvancedWindstormDetails && (
              <AdvancedWindstormDetails fromPolicy={fromPolicy} />
            )}
            <FormField
              name={`${id}.fireHydrantWithin1000ft`}
              id={`${id}.fireHydrantWithin1000ft`}
              type="checkbox"
              label="Fire hydrant within 1,000 feet?"
              mode="light"
              xs={12}
            />
            <ProtectionClassOverride fieldName={id} computedFpc={home.fpc} />
            <FormField
              name={`${id}.wildfireHazardScore`}
              type="value"
              label="Wildfire Hazard Score"
              value={home.wildfireHazardScore || 'N/A'}
              xs={4}
            />
            <FormField
              name={`${id}.wildfireGallagherScore`}
              type="value"
              label="Wildfire Gallagher Score"
              value={home.wildfireGallagherScore || 'N/A'}
              xs={4}
            />
            <FormField
              name={`${id}.dtoName`}
              type="value"
              label="Distance to Ocean"
              value={home.dtoName || 'N/A'}
              xs={4}
            />
            <FormField
              name={`${id}.dtbowLabel`}
              type="value"
              label="Nearest BOW"
              value={home.dtbowLabel || 'N/A'}
              xs={4}
            />
            <FormField
              name={`${id}.dtbowName`}
              type="value"
              label="Distance to BOW"
              value={home.dtbowName || 'N/A'}
              xs={4}
            />
            {!showStormShuttersInRoofSection && showAdvancedWindstormDetails && (
              <AdvancedWindstormDetails fromPolicy={fromPolicy} />
            )}

            <NotificationCard className={classes.notification} icon={InfoBlackIcon} iconSize="icon16" type="infoBeige">
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid container xs={8} className={classes.rceValues} alignItems="center">
                  <Label type="body2">Replacement cost estimate</Label>
                  <Label type="body3Italic">
                    {usingAgentReplacementCost ? '(Your value)' : '(Our calculated value)'}
                  </Label>
                  <Tooltip
                    iconSize="icon16"
                    smallIcon={{ src: QuestionIcon, alt: 'info icon' }}
                    text={replacementCostTooltipText}
                  />
                </Grid>
                <Grid container xs={4} direction="column">
                  <Grid container justifyContent="flex-end" className={classes.rceValues} alignItems="center">
                    <Label>RCE</Label>
                    <Label type="infoValueBold">{estimatedCost || 'N/A'}</Label>
                    {canEditReplacementCost && (
                      <IconButton
                        className={classes.rceEditButton}
                        onClick={() => setRceModalOpen(true)}
                        aria-label="Edit replacement cost estimate"
                      >
                        <img src={PencilIcon} alt="" className={classes.rceEditIcon} />
                      </IconButton>
                    )}
                    {showReplacementCostControl && !canEditReplacementCost && haveHomeReportsRun(skippedDataPulls) && (
                      <Tooltip
                        text={tooltipHoverTexts.rceLockedText(visibleValuationId)}
                        iconSize="icon16"
                        smallIcon={{ src: LockedIcon, alt: 'Lock icon' }}
                      />
                    )}
                  </Grid>
                  {usingAgentReplacementCost && (
                    <Grid container justifyContent="flex-end" className={classes.rceOurValue}>
                      <span>
                        <Label type="body3Italic">{'Our value is '}</Label>
                        <Label type="body3ItalicSemibold">
                          {currencyFormatter(ourReplacementCost, { removeDecimal: true })}
                        </Label>
                      </span>
                      <Tooltip
                        text={tooltipHoverTexts.rceOurValueText(visibleValuationId)}
                        iconSize="icon16"
                        smallIcon={{ src: QuestionIcon, alt: 'info icon' }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </NotificationCard>
            {rceModalOpen && (
              <RCEModal
                onClose={() => setRceModalOpen(false)}
                onSubmit={handleReplacementCostChange}
                calculatedReplacementCost={ourReplacementCost}
                agentReplacementCost={agentReplacementCost}
                useCalculated={useCalculatedReplacementCost}
              />
            )}
          </Grid>
        </Grid>
        <Grid container className={classes.mortgagesContainer}>
          <FormList
            classes={{ container: classes.container }}
            disabled={disabled}
            id={`${id}.mortgageDetails`}
            item={MortgageItem}
            type="InnerSection"
            addForm={AddMortgage}
            title="Mortgage lender information"
            singular="Mortgage"
            plural="Mortgages"
          />
        </Grid>
      </CollapsibleCard>
    </Section>
  );
});

Detail.propTypes = {
  disabled: PropTypes.bool,
  fromPolicy: PropTypes.bool
};

Detail.defaultProps = {
  disabled: false,
  fromPolicy: false
};

export default withDatePicker(Detail);
